import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Typography, Paper, Grid, Select, MenuItem } from '@mui/material';

// CustomCard overall mini cards
export const CustomCard = ({ metric }) => {
  const IconComp = metric.icon;
  const val = metric.value || 0;
  const lastVal = metric.lastValue || 0;
  const total = metric.total || 0;
  
  const hasComparison = metric.lastValue !== undefined && metric.total !== undefined;
  
  const diff = val - lastVal;
  const percent = lastVal !== 0 ? ((diff / lastVal) * 100).toFixed(1) : (val > 0 ? "100" : "0");
  const isPositive = diff >= 0;

  return (
    <Grid item xs={12} sm={6} md={4} lg={3}>
      <Link to={metric.link || "#"} style={{ textDecoration: "none" }}>
        <Paper
          id={metric.id}
          variant="outlined"
          sx={{
            p: 2,
            borderRadius: 2,
            boxShadow: 1,
            transition: "all 200ms ease",
            "&:hover": { boxShadow: 3, transform: 'translateY(-2px)' },
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ display: "flex", alignItems: "start", justifyContent: "space-between", mb: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={{ width: 44, height: 44, borderRadius: 1.5, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: metric.bg }}>
                <IconComp sx={{ color: metric.color, fontSize: 24 }} />
              </Box>
              <Typography sx={{ fontSize: "0.875rem", fontWeight: 550, color: "text.primary", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                {metric.label}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", gap: 1, justifyContent: "end" }}>
            {metric.userName && (
              <Typography sx={{ fontSize: "0.95rem", fontWeight: 600, color: "text.primary", mb: 0.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {metric.userName}
              </Typography>
            )}

            <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
              <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                {metric.valueLabel || "Count"}
              </Typography>
              <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, color: "text.primary" }}>
                {val}
              </Typography>
            </Box>

            {hasComparison && (
              <>
                <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
                  <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>{metric.lastValueLabel || "Last Month"}</Typography>
                  <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: "text.secondary" }}>
                    {lastVal}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
                  <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>Total</Typography>
                  <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: "text.secondary" }}>
                    {total}
                  </Typography>
                </Box>
                <Box sx={{ pt: 1, borderTop: "1px solid", borderColor: "divider", mt: 0.5 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "space-between" }}>
                    <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>Change</Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: isPositive ? "success.main" : "error.main" }}>
                        {isPositive ? "+" : ""}{diff}
                      </Typography>
                      <Box sx={{
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        px: 1,
                        py: 0.5,
                        borderRadius: 999,
                        bgcolor: isPositive ? "rgba(46, 125, 50, 0.1)" : "rgba(211, 47, 47, 0.1)",
                        color: isPositive ? "success.main" : "error.main"
                      }}>
                        {isPositive ? "+" : ""}{percent}%
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </>
            )}
          </Box>
        </Paper>
      </Link>
    </Grid>
  );
};

// SectionHeader component 
export const SectionHeader = ({ title, action, mt = 5 }) => (
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt, mb: 2 }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      <Box sx={{ height: 24, width: 4, bgcolor: "primary.main", borderRadius: "999px" }} />
      <Typography variant="h6" sx={{ fontWeight: 600, color: "text.primary" }}>
        {title}
      </Typography>
    </Box>
    {action && <Box>{action}</Box>}
  </Box>
);

// graphical cards for all 3 graphs
export const SummarySection = ({ 
  controls, 
  chart, 
  stats, 
  summaryTitle, 
  footerNote, 
  footerIconColor = "warning.main",
  chartColSize = 5,
  statsColSize = 7,
  paperSx = { p: 3, borderRadius: 2, boxShadow: 1, borderColor: "divider" }
}) => {
  return (
    <>
      <Box>
        <Paper variant="outlined" sx={paperSx}>
          {controls && <Box sx={{ mb: 3, display: "flex", justifyContent: "left" }}>{controls}</Box>}
          
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={chartColSize}>
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 2 }}>
                {chart}
              </Box>
            </Grid>
            <Grid item xs={12} md={statsColSize}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "text.secondary", mb: 1 }}>
                  {summaryTitle}
                </Typography>

                <Grid container spacing={2}>
                  {stats.map((stat, i) => (
                    <Grid item xs={6} key={i}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: 2, borderRadius: 2, bgcolor: stat.bg }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: "text.secondary", textTransform: "uppercase", letterSpacing: "1px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {stat.label}
                          </Typography>
                          <Typography sx={{ fontSize: "1.6rem", fontWeight: 800, color: stat.color, lineHeight: 1.2, mt: 0.3 }}>
                            {stat.value}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
                {footerNote && (
                  <Box sx={{ mt: 1, p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
                    <Typography component="div" variant="body2" sx={{ color: "text.secondary", display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{ width: 4, height: 16, bgcolor: footerIconColor, borderRadius: 1 }} />
                      {footerNote}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </>
  );
};

// project statistic horizontal bar
export const ProjectStatistics = ({ 
  title = "Projects Statistics", 
  stats, 
  timeframe, 
  onTimeframeChange,
  showVerticalLabel = true
}) => {
  const segments = [
    {
      label: "Completed",
      value: stats.completed,
      percentage: stats.percentages.completed,
      color: "#2e7d32",
      textColor: "#ffffff",
    },
    {
      label: "Ongoing",
      value: stats.ongoing,
      percentage: stats.percentages.ongoing,
      color: "#1976d2",
      textColor: "#ffffff",
    },
    {
      label: "Pending",
      value: stats.pending,
      percentage: stats.percentages.pending,
      color: "#94a3b8",
      textColor: "#475569",
    },
  ];

  return (
    <>
      <SectionHeader
        title={title}
        action={
          <Select
            value={timeframe}
            onChange={(e) => onTimeframeChange(e.target.value)}
            size="small"
            sx={{ minWidth: 150, bgcolor: "background.paper", borderRadius: 2 }}
          > 
            <MenuItem value="today">Today</MenuItem>
            <MenuItem value="monthly">This Month</MenuItem>
            <MenuItem value="overall">Overall</MenuItem>
          </Select>
        }
      />
      <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 3 }, borderRadius: 2, boxShadow: 1, borderColor: "divider" }}>
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          {showVerticalLabel && (
            <Typography
              sx={{
                writingMode: 'vertical-rl',
                transform: 'rotate(180deg)',
                fontWeight: 600,
                color: '#94a3b8',
                fontSize: '0.75rem',
                fontFamily: '"Inter", sans-serif',
                letterSpacing: '0.15em',
                py: 1,
                display: 'block',
                mr: { xs: 1, sm: 2 }
              }}
            >
              PROJECTS
            </Typography>
          )}

          <Box sx={{ flexGrow: 1, minWidth: 0, pl: { xs: 0.5, sm: showVerticalLabel ? 2 : 0 } }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 2,
                mb: 2,
              }}
            >
              <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography sx={{ fontSize: "0.875rem", fontWeight: 800, color: "#2e7d32" }}>{stats.completed}</Typography>
                  <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: "#64748b" }}>Completed</Typography>
                </Box>
                <Typography sx={{ fontSize: "0.875rem", color: "#cbd5e1" }}>/</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography sx={{ fontSize: "0.875rem", fontWeight: 800, color: "#1976d2" }}>{stats.ongoing}</Typography>
                  <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: "#64748b" }}>Ongoing</Typography>
                </Box>
                <Typography sx={{ fontSize: "0.875rem", color: "#cbd5e1" }}>/</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography sx={{ fontSize: "0.875rem", fontWeight: 800, color: "#94a3b8" }}>{stats.pending}</Typography>
                  <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: "#64748b" }}>Pending</Typography>
                </Box>
              </Box>
              <Typography 
                sx={{ 
                  fontSize: "0.875rem", 
                  fontWeight: 700, 
                  color: "#475569",
                  bgcolor: "#f1f5f9",
                  px: 2,
                  py: 0.75,
                  borderRadius: 1.5
                }}
              >
                Total: {stats.total}
              </Typography>
            </Box>

            <Box
              sx={{
                width: "100%",
                height: 48,
                backgroundColor: "#f1f5f9",
                borderRadius: "24px",
                overflow: "hidden",
                display: "flex",
                boxShadow: "inset 0 2px 4px rgba(0,0,0,0.05)",
                borderRadius: "18px",
                mb: 2
              }}
            >
              {segments.map((segment) => (
                <Box
                  key={segment.label}
                  sx={{
                    width: `${segment.percentage}%`,
                    backgroundColor: segment.color,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: segment.textColor,
                    fontSize: "1rem",
                    fontWeight: 800,
                    transition: "width 0.6s ease-in-out",
                  }}
                >
                  {segment.percentage >= 8 && segment.value > 0 ? segment.value : ""}
                </Box>
              ))}
            </Box>

            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 3 }}>
              {segments.map((segment) => (
                <Box key={segment.label} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 12, height: 12, backgroundColor: segment.color, borderRadius: "3px" }} />
                  <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#64748b" }}>{segment.label}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Paper>
    </>
  );
};
